import React from 'react';
import { Link } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Breadcrumbs from '@/components/Breadcrumbs';
import useRoutes from '@/hooks/useRoutes';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import {
    ArrowLeft,
    CheckCircle,
    XCircle,
    MinusCircle,
    Clock,
    Award,
} from 'lucide-react';

interface AnswerData {
    id: number;
    answer: string;
    is_correct: boolean;
    was_selected: boolean;
    image_url: string | null;
}

interface QuestionData {
    id: number;
    question: string;
    image_url: string | null;
    score: number;
    allow_multiple_answers: boolean;
    is_correct: boolean;
    was_answered: boolean;
    answers: AnswerData[];
}

interface AttemptData {
    id: number;
    evaluation_title: string;
    course_title: string;
    score: number;
    total_score: number;
    percentage: number;
    passed: boolean;
    started_at: string | null;
    completed_at: string | null;
    time_taken_minutes: number | null;
}

interface SummaryData {
    total_questions: number;
    correct_count: number;
    incorrect_count: number;
    unanswered_count: number;
}

interface StudentData {
    id: number;
    full_name: string;
    email: string;
    avatar_url: string | null;
}

interface EvaluationAttemptDetailProps {
    student: StudentData;
    attempt: AttemptData;
    summary: SummaryData;
    questions: QuestionData[];
}

const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getQuestionStatusClasses = (question: QuestionData) => {
    if (!question.was_answered) {
        return {
            border: 'border-gray-300',
            header: 'bg-gray-50 text-gray-700',
            icon: <MinusCircle size={20} className="text-gray-400" />,
            label: 'Sin responder',
        };
    }
    if (question.is_correct) {
        return {
            border: 'border-green-300',
            header: 'bg-green-50 text-green-800',
            icon: <CheckCircle size={20} className="text-green-600" />,
            label: 'Correcta',
        };
    }
    return {
        border: 'border-red-300',
        header: 'bg-red-50 text-red-800',
        icon: <XCircle size={20} className="text-red-600" />,
        label: 'Incorrecta',
    };
};

const getAnswerClasses = (answer: AnswerData) => {
    if (answer.was_selected && answer.is_correct) {
        return 'bg-green-100 border-green-400 text-green-900';
    }
    if (answer.was_selected && !answer.is_correct) {
        return 'bg-red-100 border-red-400 text-red-900';
    }
    if (!answer.was_selected && answer.is_correct) {
        return 'border-green-400 border-dashed bg-green-50 text-green-800';
    }
    return 'border-gray-200 bg-white text-gray-700';
};

const DetailContent: React.FC<EvaluationAttemptDetailProps> = ({
    student,
    attempt,
    summary,
    questions,
}) => {
    const { route } = useRoutes();

    return (
        <StaffLayout>
            <div className="mx-auto max-w-5xl">
                {/* Breadcrumbs */}
                <Breadcrumbs
                    items={[
                        {
                            label: 'Estudiantes',
                            href: route('staff-students').toString(),
                        },
                        {
                            label: student.full_name,
                            href: route('staff-students-detail', {
                                student_id: student.id,
                            }).toString(),
                        },
                        {
                            label: attempt.evaluation_title,
                        },
                    ]}
                    className="mb-6"
                />

                {/* Header */}
                <div className="mb-6">
                    <Link
                        href={`${route('staff-students-detail', {
                            student_id: student.id,
                        }).toString()}?tab=evaluations`}
                        className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                        <ArrowLeft size={16} className="mr-1" />
                        Volver al perfil del estudiante
                    </Link>
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {attempt.evaluation_title}
                            </h1>
                            <p className="mt-1 text-sm text-gray-600">
                                {attempt.course_title} &middot; {student.full_name}
                            </p>
                        </div>
                        <span
                            className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                                attempt.passed
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                            }`}
                        >
                            {attempt.passed ? 'Aprobado' : 'Reprobado'}
                        </span>
                    </div>
                </div>

                {/* Summary Card */}
                <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                        {/* Percentage */}
                        <div className="text-center">
                            <div
                                className={`text-3xl font-bold ${
                                    attempt.passed ? 'text-green-600' : 'text-red-600'
                                }`}
                            >
                                {attempt.percentage}%
                            </div>
                            <div className="mt-1 text-sm text-gray-500">Calificación</div>
                        </div>

                        {/* Points */}
                        <div className="text-center">
                            <div className="flex items-center justify-center text-3xl font-bold text-gray-900">
                                <Award size={24} className="mr-2 text-gray-400" />
                                {attempt.score}/{attempt.total_score}
                            </div>
                            <div className="mt-1 text-sm text-gray-500">Puntos</div>
                        </div>

                        {/* Correct/Incorrect/Unanswered */}
                        <div className="text-center">
                            <div className="flex items-center justify-center space-x-3">
                                <span className="flex items-center text-green-600">
                                    <CheckCircle size={16} className="mr-1" />
                                    {summary.correct_count}
                                </span>
                                <span className="flex items-center text-red-600">
                                    <XCircle size={16} className="mr-1" />
                                    {summary.incorrect_count}
                                </span>
                                <span className="flex items-center text-gray-400">
                                    <MinusCircle size={16} className="mr-1" />
                                    {summary.unanswered_count}
                                </span>
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                                de {summary.total_questions} preguntas
                            </div>
                        </div>

                        {/* Time */}
                        <div className="text-center">
                            <div className="flex items-center justify-center text-lg font-semibold text-gray-900">
                                <Clock size={20} className="mr-2 text-gray-400" />
                                {attempt.time_taken_minutes !== null
                                    ? `${attempt.time_taken_minutes} min`
                                    : '-'}
                            </div>
                            <div className="mt-1 text-sm text-gray-500">Tiempo</div>
                            <div className="mt-0.5 text-xs text-gray-400">
                                {formatDateTime(attempt.started_at)} &mdash;{' '}
                                {formatDateTime(attempt.completed_at)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Questions List */}
                <h2 className="mb-4 text-lg font-semibold text-gray-900">
                    Preguntas ({questions.length})
                </h2>
                <div className="space-y-4">
                    {questions.map((question, index) => {
                        const status = getQuestionStatusClasses(question);
                        return (
                            <div
                                key={question.id}
                                className={`overflow-hidden rounded-lg border-2 ${status.border}`}
                            >
                                {/* Question Header */}
                                <div
                                    className={`flex items-center justify-between px-4 py-3 ${status.header}`}
                                >
                                    <div className="flex items-center space-x-2">
                                        {status.icon}
                                        <span className="font-medium">
                                            Pregunta {index + 1}
                                        </span>
                                        <span className="text-sm opacity-75">
                                            ({status.label})
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium">
                                        {question.score}{' '}
                                        {question.score === 1 ? 'punto' : 'puntos'}
                                    </span>
                                </div>

                                {/* Question Body */}
                                <div className="bg-white p-4">
                                    <div
                                        className="html-content mb-4 max-w-none text-sm text-gray-800"
                                        dangerouslySetInnerHTML={{
                                            __html: cleanWordPressHtml(
                                                question.question,
                                            ),
                                        }}
                                    />

                                    {question.image_url && (
                                        <img
                                            src={question.image_url}
                                            alt="Imagen de la pregunta"
                                            className="mb-4 max-h-64 rounded-lg"
                                        />
                                    )}

                                    {question.allow_multiple_answers && (
                                        <p className="mb-3 text-xs italic text-gray-500">
                                            Respuesta múltiple
                                        </p>
                                    )}

                                    {/* Answer Options */}
                                    <div className="space-y-2">
                                        {question.answers.map((answer) => {
                                            const answerClasses =
                                                getAnswerClasses(answer);
                                            return (
                                                <div
                                                    key={answer.id}
                                                    className={`flex items-start rounded-lg border px-4 py-3 ${answerClasses}`}
                                                >
                                                    <div className="mr-3 mt-0.5 flex-shrink-0">
                                                        {answer.was_selected &&
                                                            answer.is_correct && (
                                                                <CheckCircle
                                                                    size={18}
                                                                    className="text-green-600"
                                                                />
                                                            )}
                                                        {answer.was_selected &&
                                                            !answer.is_correct && (
                                                                <XCircle
                                                                    size={18}
                                                                    className="text-red-600"
                                                                />
                                                            )}
                                                        {!answer.was_selected &&
                                                            answer.is_correct && (
                                                                <CheckCircle
                                                                    size={18}
                                                                    className="text-green-400"
                                                                />
                                                            )}
                                                        {!answer.was_selected &&
                                                            !answer.is_correct && (
                                                                <div className="h-[18px] w-[18px]" />
                                                            )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="html-content max-w-none text-sm">
                                                            {cleanWordPressHtml(answer.answer)}
                                                        </div>
                                                        {answer.image_url && (
                                                            <img
                                                                src={answer.image_url}
                                                                alt="Imagen de la respuesta"
                                                                className="mt-2 max-h-40 rounded"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </StaffLayout>
    );
};

const EvaluationAttemptDetail: React.FC<EvaluationAttemptDetailProps> = (props) => {
    return (
        <ThemeWrapper>
            <DetailContent {...props} />
        </ThemeWrapper>
    );
};

export default EvaluationAttemptDetail;
